import {
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import isEmpty from "lodash/isEmpty";
import { useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { CiEdit } from "react-icons/ci";
import { FaRegSave } from "react-icons/fa";
import { MdDeleteOutline } from "react-icons/md";
import Skeleton from "react-loading-skeleton";
import Swal from "sweetalert2";
import Header from "../../components/header";
import { fetcher } from "../../network";
import { API } from "../../network/api";
import { useHomeState } from "../../states/homeState";
import { Home, RoleType } from "../../types";

const setupTwoFactorQuery = async (
  homeId: string,
  authUsername: string,
  role: RoleType | ""
) => {
  const { data } = await fetcher().post<{ qrCodeURL: string }>(
    API.POST.SETUP_2FA,
    {
      homeId,
      authUsername,
      role,
    }
  );

  return data;
};

const getHome = async (homeId: string) => {
  const { data } = await fetcher().get<{ home: Home }>(API.GET.HOME(homeId));
  return data.home;
};

const roles: { type: RoleType; title: string }[] = [
  { type: "admin", title: "מנהל" },
  { type: "guide", title: "מדריך" },
  { type: "psychiatrist", title: "פסיכיאטר" },
];

const AdminPannel = () => {
  const homeId = useHomeState((state) => state.homeId);
  const [showSetup, setShowSetup] = useState<boolean>(false);
  const [authUsername, setAuthUsername] = useState<string>("");
  const [role, setRole] = useState<RoleType | "">("");
  const [editingModeIndex, setEditingModeIndex] = useState<number>(-1);
  const [newRole, setNewRole] = useState<RoleType | "">("");

  if (!homeId) {
    return <p>Error</p>;
  }

  const {
    data: home,
    isLoading: isLoadingHomeData,
    error: homeError,
  } = useQuery({
    queryKey: ["getHome", homeId],
    queryFn: () => getHome(homeId),
  });

  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["twoFactorAuth", "setup", "admin", homeId],
    queryFn: () => setupTwoFactorQuery(homeId, authUsername, role),
    enabled: showSetup && !isEmpty(authUsername),
  });

  const { mutate: removeAuthKey } = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await fetcher().delete<{ home: Home }>(
        API.DELETE.REMOVE_AUTH_KEY(id)
      );
      return data.home;
    },
    onSuccess: ({ twoFactorAuthSecrets }) => {
      queryClient.setQueryData(["getHome", homeId], (old: Home) => {
        if (!old) {
          return old;
        }

        old.twoFactorAuthSecrets = twoFactorAuthSecrets;
      });
      toast.success("ההרשאה נמחקה בהצלחה");
    },
    onError: () => {
      toast.error("שגיאה..");
    },
  });

  const { mutate: editRole } = useMutation({
    mutationFn: async ({ id, role }: { id: string; role: RoleType }) => {
      const { data } = await fetcher().put<{ home: Home }>(
        API.PUT.EDIT_USER_ROLE(id),
        { role }
      );
      return data.home;
    },
    onSuccess: ({ twoFactorAuthSecrets }) => {
      queryClient.setQueryData(["getHome", homeId], (old: Home) => {
        if (!old) {
          return old;
        }

        old.twoFactorAuthSecrets = twoFactorAuthSecrets;
      });
      toast.success("ההרשאה נערכה בהצלחה");
    },
    onError: () => {
      toast.error("שגיאה..");
    },
  });

  if (isLoadingHomeData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <CircularProgress />
      </div>
    );
  }

  if (homeError) {
    return <p className="text-red-500 text-center">Error loading data</p>;
  }

  if (!home || !home.twoFactorAuthSecrets.length) {
    return (
      <div className="text-center text-gray-500 mt-10">
        No two-factor authentication records found.
      </div>
    );
  }

  const toggleEditingMode = (index: number) => {
    setEditingModeIndex((prev) => (prev === index ? -1 : index));
  };

  const handleChange = (value: string) => {
    setNewRole(value as RoleType | "");
  };

  const handleSaveClick = (secretId: string) => {
    if (newRole === "") {
      toggleEditingMode(-1);
      return;
    }
    editRole({ id: secretId, role: newRole });
    toggleEditingMode(-1);
  };

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <Header>
        <Header.Time />
        <Header.Title title="פאנל ניהול" />
        <Header.Logo />
      </Header>

      {/* 2FA Access Table */}
      <Card className="p-4 shadow-lg rounded-lg border">
        <CardContent>
          <div className="flex justify-between mb-4 items-center">
            <Typography variant="h5" className="mb-4 font-semibold">
              הרשאות התחברות
            </Typography>
            <button
              className="border py-2 px-4 bg-blue-100 rounded"
              onClick={() => {
                Swal.fire({
                  title: "הזן שם משתמש",
                  html: `
                    <input id="username" class="swal2-input" type="text" placeholder="שם משתמש" value="${authUsername}">
                    <label style="display: block;">בחר הרשאת משתמש</label>
                    <select style="margin-top: 2px; border: 1px solid gray; border-radius: 4px; padding: 0px 2px;" id="role" class="swal2-input">
                      ${roles
                        .map(
                          (role) =>
                            `<option value="${role.type}">${role.title}</option>`
                        )
                        .join("")}
                    </select>
                  `,
                  confirmButtonText: "אישור",
                  cancelButtonText: "ביטול",
                  showCancelButton: true,
                  preConfirm: () => {
                    const username = document.getElementById(
                      "username"
                    ) as HTMLInputElement;

                    const role = document.getElementById(
                      "role"
                    ) as HTMLSelectElement;

                    if (!username.value || username.value.length < 2) {
                      Swal.showValidationMessage(
                        "חובה להזין לפחות שם 2 אותיות"
                      );
                      return false;
                    }
                    return { username: username.value, role: role.value };
                  },
                }).then((confirmation) => {
                  if (confirmation.isConfirmed) {
                    setAuthUsername(confirmation.value.username);
                    setRole(confirmation.value.role); // Set the selected role
                  }
                  setShowSetup((prev) => !prev);
                });
              }}>
              הוסף משתמש
            </button>
          </div>

          <TableContainer component={Paper} className="shadow-md rounded-lg">
            <Table>
              <TableHead>
                <TableRow className="bg-gray-100 dark:bg-gray-300">
                  <TableCell className="font-semibold">שם משתמש</TableCell>
                  <TableCell className="font-semibold">הרשאה</TableCell>
                  <TableCell className="">פעולות</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {home.twoFactorAuthSecrets.map((secret, index) => {
                  const isEditingMode = editingModeIndex === index;
                  return (
                    <TableRow key={secret._id} className="transition">
                      <TableCell className="py-2">
                        {secret.authUsername}
                      </TableCell>
                      <TableCell className="py-2">
                        {isEditingMode ? (
                          <select
                            value={newRole}
                            onChange={(evant) =>
                              handleChange(evant.target.value)
                            }
                            className="border p-1">
                            {roles.map((r, i) => (
                              <option key={i.toString()} value={r.type}>
                                {r.title}
                              </option>
                            ))}
                          </select>
                        ) : (
                          roles.find((r) => r.type === secret.role)?.title || ""
                        )}
                      </TableCell>
                      <TableCell className="text-left">
                        {secret.authUsername !== "admin" && (
                          <div className="flex gap-6">
                            {isEditingMode && (
                              <Tooltip title="שמור שינויים">
                                <IconButton
                                  className="hover:bg-red-500"
                                  color="success"
                                  size="medium"
                                  onClick={() => {
                                    handleSaveClick(secret._id);
                                  }}>
                                  <FaRegSave />
                                  {"  "}
                                  <span className="text-sm">שמור שינויים</span>
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="עריכת הרשאה">
                              <IconButton
                                className="hover:bg-red-500"
                                color="default"
                                size="medium"
                                onClick={() => toggleEditingMode(index)}>
                                <CiEdit />{" "}
                                <span className="text-sm">
                                  {isEditingMode
                                    ? "ביטול עריכה"
                                    : "עריכת הרשאה"}
                                </span>
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="מחיקת הרשאה">
                              <IconButton
                                className="hover:bg-red-500"
                                color="error"
                                size="medium"
                                onClick={() => {
                                  removeAuthKey(secret._id);
                                }}>
                                <MdDeleteOutline />{" "}
                                <span className="text-sm">מחיקת הרשאה</span>
                              </IconButton>
                            </Tooltip>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {!isEmpty(authUsername) && !error && (
        <div className="mt-10">
          <div className="text-center flex flex-col items-center">
            <p className="text-gray-600 mb-4">
              סרוק את ה-QR קוד באמצעות אפליקצית Google Authenticator כדי להגדיר
              את האימות הדו-שלבי.
            </p>

            <div className="flex justify-center items-center h-48 w-48 border-2 border-dashed border-gray-400 rounded-md mb-6">
              {isLoading || !data ? (
                <Skeleton count={1} height={48} width={48} />
              ) : (
                <img src={data.qrCodeURL} alt="2fa qr code url" />
              )}
            </div>
          </div>
        </div>
      )}
      <Toaster position="bottom-center" />
    </div>
  );
};

export default AdminPannel;
